/**
 * Agent Configuration Store (Zustand)
 * 
 * ARCHITECTURE: Cloud-synced workflow presets
 * 
 * - System presets: Read-only for regular users, managed by superadmins
 * - User presets: User's own customizations, saved to cloud
 * - Fork on edit: When modifying a system preset, creates a user copy
 */
import { create } from 'zustand';
import { agentConfigApi } from '../services/cloudApi';

// ============================================================================
// FALLBACK WORKFLOW (used when cloud is unavailable)
// ============================================================================

const FALLBACK_WORKFLOW = {
  nodes: [
    {
      id: 'builder',
      type: 'agent',
      position: { x: 400, y: 100 },
      data: {
        label: 'Builder',
        isEntryPoint: true,
        color: '#10B981',
        config: {
          name: 'builder',
          description: 'Main agent - builds 3D configurators',
          model: {
            provider: 'gemini-vertex',
            model_id: 'gemini-3.8-flash',
            temperature: 0.3,
            max_tokens: 64000,
          },
          system_prompt: '# Builder Agent\n\nYou build 3D product configurators.',
          tools: [
            'sandboxed_file_read',
            'sandboxed_file_write',
            'sandboxed_file_copy',
            'sandboxed_file_move',
            'sandboxed_editor',
            'sandboxed_shell',
            'u3d_browser',
            'unlimited3d_kb',
            'view_image',
            'search_components',
            'get_component_info',
            'get_component_recipe',
            'name_project',
          ],
        },
      },
    },
  ],
  edges: [],
  customTools: [],
  metadata: {
    type: 'single',
    name: 'Default Builder (Offline Mode)',
    description: 'Basic builder when cloud is unavailable. Connect to cloud to access full workflow presets.',
    version: '1.0',
  },
  swarm_config: {
    max_handoffs: 10,
    max_iterations: 15,
    execution_timeout: 600.0,
    node_timeout: 300.0,
  },
};

// ============================================================================
// ZUSTAND STORE
// ============================================================================

// Concurrent initialize() callers (MainApp mount + homepage handoff) share one
// request instead of one silently overwriting the other.
let _initPromise = null;

export const useAgentConfigStore = create((set, get) => ({
  // Current workflow state
  workflow: FALLBACK_WORKFLOW,
  workflowId: null,           // UUID of current workflow (null = unsaved/local)
  workflowName: 'Loading...', // Display name
  isSystem: false,            // True if this is a system preset (read-only)
  isDirty: false,             // True if there are unsaved changes
  
  // Available presets from cloud
  presets: [],                // [{id, name, description, is_system, is_default, ...}]
  presetsLoading: false,
  presetsError: null,
  
  // Initialization state
  initialized: false,
  initError: null,

  // ============================================================================
  // GETTERS
  // ============================================================================

  getWorkflow: () => get().workflow,
  
  getWorkflowName: () => get().workflowName,
  
  getWorkflowForExport: () => {
    const { workflow } = get();
    return {
      nodes: workflow.nodes,
      edges: workflow.edges,
      customTools: workflow.customTools || [],
      metadata: workflow.metadata || {},
      swarm_config: workflow.swarm_config || {},
    };
  },
  
  getAgentConfig: (agentId) => {
    const { workflow } = get();
    const node = workflow.nodes.find((n) => n.id === agentId);
    return node?.data?.config || null;
  },
  
  getNode: (nodeId) => {
    const { workflow } = get();
    return workflow.nodes.find((n) => n.id === nodeId);
  },

  // ============================================================================
  // WORKFLOW MODIFICATION (marks as dirty)
  // ============================================================================

  setWorkflow: (workflow) => set({ 
    workflow, 
    isDirty: true,
    workflowName: workflow.metadata?.name || get().workflowName,
  }),
  
  // Sync workflow without marking as dirty (for sync effects after save/load)
  syncWorkflow: (workflow) => set({ 
    workflow,
    workflowName: workflow.metadata?.name || get().workflowName,
  }),
  
  updateNode: (nodeId, updates) => set((state) => ({
    workflow: {
      ...state.workflow,
      nodes: state.workflow.nodes.map((node) =>
        node.id === nodeId ? { ...node, ...updates } : node
      ),
    },
    isDirty: true,
  })),

  setWorkflowMetadata: (metadata) => set((state) => ({
    workflow: {
      ...state.workflow,
      metadata: { ...state.workflow.metadata, ...metadata },
    },
    workflowName: metadata.name || state.workflowName,
    isDirty: true,
  })),

  // ============================================================================
  // CLOUD SYNC - INITIALIZATION
  // ============================================================================

  /**
   * Initialize the store by fetching the default workflow from cloud
   */
  initialize: async () => {
    if (get().initialized) return;
    if (_initPromise) return _initPromise;

    _initPromise = (async () => {
      try {
        const defaultConfig = await agentConfigApi.getDefault();

        if (defaultConfig) {
          const workflow = defaultConfig.workflow_definition;
          set({
            workflow,
            workflowId: defaultConfig.id,
            workflowName: defaultConfig.name,
            isSystem: defaultConfig.is_system,
            isDirty: false,
            initialized: true,
            initError: null,
          });
        } else {
          set({
            workflow: FALLBACK_WORKFLOW,
            workflowId: null,
            workflowName: FALLBACK_WORKFLOW.metadata.name,
            isSystem: false,
            isDirty: false,
            initialized: true,
            initError: 'No presets available',
          });
        }
      } catch (error) {
        console.error('Failed to initialize agent config:', error);
        set({
          workflow: FALLBACK_WORKFLOW,
          workflowId: null,
          workflowName: FALLBACK_WORKFLOW.metadata.name,
          isSystem: false,
          isDirty: false,
          initialized: true,
          initError: error.message,
        });
      }
    })();

    try {
      await _initPromise;
    } finally {
      _initPromise = null;
    }
  },

  /**
   * Load the correct default preset for a given project template.
   * Called when a project is opened/created so the active preset matches the template.
   * Skips if the current preset already targets the right template.
   */
  loadDefaultForTemplate: async (template) => {
    if (!template) return;

    try {
      const defaultConfig = await agentConfigApi.getDefault(template);
      if (!defaultConfig) return;
      if (get().workflowId === defaultConfig.id) return;
      set({
        workflow: defaultConfig.workflow_definition,
        workflowId: defaultConfig.id,
        workflowName: defaultConfig.name,
        isSystem: defaultConfig.is_system,
        isDirty: false,
      });
    } catch (error) {
      console.error('Failed to load default preset for template:', template, error);
    }
  },

  // ============================================================================
  // CLOUD SYNC - FETCH PRESETS
  // ============================================================================

  /**
   * Fetch all available presets (user's + system)
   * @param {string|null} targetTemplate - Filter by project template (e.g. 'unlimited3d', 'threejs'). Null = all presets.
   */
  fetchPresets: async (targetTemplate = null) => {
    set({ presetsLoading: true, presetsError: null });
    
    try {
      const response = await agentConfigApi.list({ includeSystem: true, targetTemplate });
      const configs = response.configs || [];
      set({ 
        presets: configs, 
        presetsLoading: false,
      });
      const current = configs.find((p) => p.id === get().workflowId);
      if (current?.locked) {
        await get().loadDefaultForTemplate(targetTemplate);
      }
      return configs;
    } catch (error) {
      console.error('Failed to fetch presets:', error);
      set({ 
        presetsError: error.message, 
        presetsLoading: false,
      });
      return [];
    }
  },

  // ============================================================================
  // CLOUD SYNC - LOAD PRESET
  // ============================================================================

  /**
   * Load a specific preset by ID
   */
  loadPreset: async (presetId) => {
    try {
      const config = await agentConfigApi.get(presetId);
      
      if (config) {
        const workflow = config.workflow_definition;
        set({
          workflow,
          workflowId: config.id,
          workflowName: config.name,
          isSystem: config.is_system,
          isDirty: false,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to load preset:', error);
      throw error;
    }
  },

  // ============================================================================
  // CLOUD SYNC - SAVE PRESET
  // ============================================================================

  /**
   * Save current workflow
   * - If it's a user preset: update it
   * - If it's a system preset: fork it (create new user preset)
   * - If it's new: create it
   */
  savePreset: async (name = null) => {
    const { workflow, workflowId, isSystem, workflowName } = get();
    
    const presetData = {
      name: name || workflowName,
      description: workflow.metadata?.description || '',
      is_default: false,
      workflow_type: workflow.metadata?.type || 'swarm',
      workflow_definition: {
        nodes: workflow.nodes,
        edges: workflow.edges,
        customTools: workflow.customTools || [],
        metadata: { ...workflow.metadata, name: name || workflowName },
        swarm_config: workflow.swarm_config || {},
      },
    };
    
    try {
      let savedConfig;
      
      if (workflowId && !isSystem) {
        // Update existing user preset
        savedConfig = await agentConfigApi.update(workflowId, presetData);
      } else {
        // Create new preset (fork from system or create new)
        savedConfig = await agentConfigApi.create(presetData);
      }
      
      set({
        workflowId: savedConfig.id,
        workflowName: savedConfig.name,
        isSystem: savedConfig.is_system,
        isDirty: false,
      });
      
      // Refresh presets list
      get().fetchPresets();
      
      return savedConfig;
    } catch (error) {
      console.error('Failed to save preset:', error);
      throw error;
    }
  },

  /**
   * Save as a new preset with a new name
   */
  saveAsNewPreset: async (name) => {
    const { workflow } = get();
    
    // Update workflow metadata with new name
    const updatedWorkflow = {
      ...workflow,
      metadata: { ...workflow.metadata, name },
    };
    
    const presetData = {
      name,
      description: updatedWorkflow.metadata?.description || '',
      is_default: false,
      workflow_type: updatedWorkflow.metadata?.type || 'swarm',
      workflow_definition: {
        nodes: updatedWorkflow.nodes,
        edges: updatedWorkflow.edges,
        customTools: updatedWorkflow.customTools || [],
        metadata: updatedWorkflow.metadata,
        swarm_config: updatedWorkflow.swarm_config || {},
      },
    };
    
    try {
      const savedConfig = await agentConfigApi.create(presetData);
      
      // Update store with new workflow (including updated metadata) and new ID
      set({
        workflow: updatedWorkflow,
        workflowId: savedConfig.id,
        workflowName: savedConfig.name,
        isSystem: false,
        isDirty: false,
      });
      
      // Refresh presets list
      get().fetchPresets();
      
      return savedConfig;
    } catch (error) {
      console.error('Failed to save as new preset:', error);
      throw error;
    }
  },

  // ============================================================================
  // CLOUD SYNC - DELETE PRESET
  // ============================================================================

  /**
   * Delete a user preset (cannot delete system presets)
   */
  deletePreset: async (presetId) => {
    try {
      await agentConfigApi.delete(presetId);
      
      // If we deleted the current preset, load default
      if (get().workflowId === presetId) {
        await get().initialize();
      }
      
      // Refresh presets list
      await get().fetchPresets();
      
      return true;
    } catch (error) {
      console.error('Failed to delete preset:', error);
      throw error;
    }
  },

  // ============================================================================
  // RESET
  // ============================================================================

  /**
   * Reset to cloud default
   */
  resetToDefault: async () => {
    set({ initialized: false });
    await get().initialize();
  },

  /**
   * Reset to local fallback (for offline mode)
   */
  resetToFallback: () => set({
    workflow: FALLBACK_WORKFLOW,
    workflowId: null,
    workflowName: FALLBACK_WORKFLOW.metadata.name,
    isSystem: false,
    isDirty: false,
  }),
}));

export default useAgentConfigStore;
